/**
 * Session context. The role lives here because it changes what the whole console renders — and per the
 * design's third load-bearing decision, a viewer sees actions DISABLED, never hidden. So `locked` is
 * derived once, here, rather than each screen inventing its own permission check.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api, getToken, setToken, SessionExpired, type ConsoleUser } from "./api";

interface SessionValue {
  user: ConsoleUser | null;
  loading: boolean;
  /** True while NO console account exists — the setup screen replaces sign-in until one does. */
  needsSetup: boolean;
  setupDone: () => void;
  /** True when the signed-in operator may not act. Managers and super admins may. */
  locked: boolean;
  signIn: (token: string, user: ConsoleUser) => void;
  signOut: () => Promise<void>;
  /** Called when any request discovers the session has died mid-task. */
  expire: () => void;
  expired: boolean;
}

const Ctx = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<ConsoleUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [expired, setExpired] = useState(false);
  const [needsSetup, setNeedsSetup] = useState(false);

  // A refreshed tab should not ask for the password again while its session is still alive.
  // Is the console set up at all? Asked before rendering a sign-in form nobody could yet pass.
  useEffect(() => {
    let cancelled = false;
    if (getToken()) return;                       // a live session means setup is long done
    api.setupStatus().then(
      (s) => { if (!cancelled) setNeedsSetup(s.needed); },
      () => { /* unreachable engine — the sign-in screen reports it */ },
    );
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!getToken()) { setLoading(false); return; }
    api.me().then(
      (u) => { if (!cancelled) { setUser(u); setLoading(false); } },
      () => { if (!cancelled) { setToken(null); setUser(null); setLoading(false); } },
    );
    return () => { cancelled = true; };
  }, []);

  const signIn = useCallback((token: string, u: ConsoleUser) => {
    setToken(token); setUser(u); setExpired(false);
  }, []);

  const signOut = useCallback(async () => {
    try { await api.signOut(); } catch { /* already gone — sign out locally regardless */ }
    setToken(null); setUser(null); setExpired(false);
  }, []);

  const expire = useCallback(() => { setToken(null); setUser(null); setExpired(true); }, []);

  const value = useMemo<SessionValue>(() => ({
    user, loading, expired, signIn, signOut, expire,
    needsSetup, setupDone: () => setNeedsSetup(false),
    locked: user ? user.role === "viewer" : true,
  }), [user, loading, expired, signIn, signOut, expire, needsSetup]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionValue {
  const v = useContext(Ctx);
  if (!v) throw new Error("useSession outside SessionProvider");
  return v;
}

/**
 * One place that turns a failed request into either an error message or a session expiry. Screens call
 * this instead of catching SessionExpired themselves, so a dead session can never be rendered as
 * "no data" — which on a money console reads as "the money is gone".
 */
export function useLoader<T>(load: () => Promise<T>, deps: unknown[]): { data: T | null; error: string | null; loading: boolean; reload: () => void } {
  const { expire } = useSession();
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    load().then(
      (d) => { if (!cancelled) { setData(d); setLoading(false); } },
      (e: unknown) => {
        if (cancelled) return;
        if (e instanceof SessionExpired) { expire(); return; }
        setError(e instanceof Error ? e.message : "Something went wrong");
        setLoading(false);
      },
    );
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  return { data, error, loading, reload: () => setNonce((n) => n + 1) };
}
